import {
  type ComponentProps,
  type ComponentType,
  type ElementType,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Typography,
  Grid as GridBase,
  Popover,
  Alert,
  Icon as IconBase,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RpMuiToolTipBase from "@/components/muiCustom/popover/RpMuiToolTip";
import { brandService } from "@/shared/services/brandService";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { Link } from "react-router-dom";
import { logger } from "@/helpers/loggerHelper";
import { processAndSortChannels, RpConfirmDialog } from "@/helpers/utils";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import RpSvgIconBase from "@/components/icons/RpSvgIcon";
import RpConfirmBase from "@/components/RpConfirm";

type Channel = {
  altText?: string;
  grayscale?: boolean;
  id: string | number;
  image?: string | number;
  name: string;
  requested_status?: unknown;
  status?: boolean | "active" | "inactive";
};

type ChannelListProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  channels?: Channel[];
  showRequestAccess?: boolean;
  showToolTip?: boolean;
  loader?: (_loading: boolean) => void;
  getBrandProfileDetails?: () => void;
  isLoading?: boolean;
};

type GlobalContextValue = {
  loggedInUser?: {
    first_name?: string;
    last_name?: string;
  };
  selectedBrand?: {
    brand_name?: string;
    id?: string | number;
    name?: string;
    status?: unknown;
  };
};

type PopoverInfo = {
  anchorEl: HTMLElement | null;
  id: string | number | null;
};

type GridProps = ComponentProps<typeof GridBase> & {
  item?: boolean;
  size?: {
    xs?: string | number;
  };
};

type IconProps = ComponentProps<typeof IconBase> & {
  component?: ElementType;
  name?: string;
};

type ToolTipProps = {
  children: ReactNode;
  content: ReactNode;
};

type TypographyProps = ComponentProps<typeof Typography> & {
  component?: ElementType;
};

type LoggerWithLog = typeof logger & {
  log?: (..._args: unknown[]) => void;
};

type ConfirmDialog = (
  _content: ReactNode,
  _onConfirm: () => void,
  _onCancel: () => void
) => void;

const Grid = GridBase as ComponentType<GridProps>;
const Icon = IconBase as ComponentType<IconProps>;
const RpMuiToolTip = RpMuiToolTipBase as ComponentType<ToolTipProps>;
const RpConfirm = RpConfirmBase as ComponentType<{
  heading?: string;
  message?: string;
}>;
const RpSvgIcon = RpSvgIconBase as ElementType;
const loggerWithLog = logger as LoggerWithLog;
const confirmDialog = RpConfirmDialog as unknown as ConfirmDialog;

// Constants for Popover positioning
const POPOVER_ANCHOR_ORIGIN = {
  vertical: "top",
  horizontal: "center",
} as const;

const POPOVER_TRANSFORM_ORIGIN = {
  vertical: "bottom",
  horizontal: "center",
} as const;

// Styled Components
const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: "bold",
})) as ComponentType<TypographyProps>;

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
})) as ComponentType<TypographyProps>;

const ChannelImage = styled("img")({
  width: 25,
  height: 25,
  objectFit: "contain",
  cursor: "pointer",
});

const Grayscale = styled("img")({
  width: 25,
  height: 25,
  objectFit: "contain",
  filter: "grayscale(100%)",
  opacity: 0.6,
  cursor: "pointer",
});

const ActiveStatus = styled("span")({
  color: "var(--status-success)",
  fontWeight: 600,
});

const InactiveStatus = styled("span")({
  color: "var(--status-error)",
  fontWeight: 600,
});

const ChannelList = ({
  title,
  subtitle,
  channels,
  showRequestAccess = true,
  showToolTip = false,
  loader = () => {},
  getBrandProfileDetails = () => {},
  isLoading = false,
}: ChannelListProps) => {
  const [popoverInfo, setPopoverInfo] = useState<PopoverInfo>({
    anchorEl: null,
    id: null,
  });
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalContextValue;

  // Process channels using the centralized helper
  const processedChannels = processAndSortChannels(channels) as Channel[];
  const hasResolvedChannels = Array.isArray(channels);
  const shouldShowEmptyState =
    !isLoading && hasResolvedChannels && processedChannels.length === 0;

  // Popover Handlers
  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>, id: string | number) => {
      setPopoverInfo({ anchorEl: event.currentTarget, id });
    },
    []
  );

  const handleClose = useCallback(() => {
    setPopoverInfo({ anchorEl: null, id: null });
  }, []);

  const handleClickOutside = useCallback(
    (event: globalThis.MouseEvent) => {
      if (
        popoverInfo.anchorEl &&
        !popoverInfo.anchorEl.contains(event.target as Node)
      ) {
        handleClose();
      }
    },
    [handleClose, popoverInfo.anchorEl]
  );

  useEffect(() => {
    if (!popoverInfo.anchorEl) return;

    const handleScroll = () => handleClose();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleClose, popoverInfo.anchorEl, handleClickOutside]);

  // Request Access API
  const handleRequestAccess = async (channel: Channel) => {
    try {
      loader(true);
      const payload = {
        brandId: selectedBrand?.id,
        userName: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
        channelName: channel.name,
        channelId: channel.id,
        brandName: selectedBrand?.brand_name || selectedBrand?.name,
      };

      const res = await brandService.requestChannelAccess(payload);

      if (res?.status === 200) {
        RpSweetAlert.fire({
          icon: "success",
          title: "Thank you!",
          text: "Your account manager will reach out to you shortly.",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
      }

      getBrandProfileDetails();
      setTimeout(() => handleClose(), 100);
      loggerWithLog.log?.("Channel access requested successfully");
    } catch (error) {
      logger.error("Error requesting channel access:", error);
    } finally {
      loader(false);
    }
  };

  return (
    <>
      <Title variant="h6" component="h2">
        {title}
      </Title>
      <Subtitle variant="caption" component="p">
        {subtitle}
      </Subtitle>

      <Grid container spacing={2} alignItems="flex-start">
        {processedChannels.length > 0 ? (
          processedChannels.map((channel) => (
            <Grid item size={{ xs: "auto" }} key={channel.id}>
              <Box alignItems="center" textAlign="center">
                {channel.grayscale ? (
                  <Grayscale
                    src={String(channel.image)}
                    alt={channel.altText}
                    onClick={(e) => handleClick(e, channel.id)}
                  />
                ) : (
                  <ChannelImage
                    src={String(channel.image)}
                    alt={channel.altText}
                    onClick={(e) => handleClick(e, channel.id)}
                  />
                )}

                <Popover
                  open={popoverInfo.id === channel.id}
                  anchorEl={popoverInfo.anchorEl}
                  onClose={handleClose}
                  anchorOrigin={POPOVER_ANCHOR_ORIGIN}
                  transformOrigin={POPOVER_TRANSFORM_ORIGIN}
                  disableScrollLock
                  keepMounted={false}
                  slotProps={{
                    paper: { sx: { zIndex: 1300, p: 1 } },
                  }}
                >
                  <Typography
                    component="div"
                    sx={{ fontSize: 12, fontWeight: 600 }}
                  >
                    {channel.name}:{" "}
                    {channel.status === "active" || channel.status === true ? (
                      <ActiveStatus>Active</ActiveStatus>
                    ) : (
                      <InactiveStatus>Inactive</InactiveStatus>
                    )}
                  </Typography>

                  {showRequestAccess &&
                    selectedBrand?.status &&
                    channel.status === "inactive" &&
                    !channel.requested_status && (
                      <Link
                        to="#"
                        style={{ color: "var(--primary-base)", fontSize: 12 }}
                        onClick={(e) => {
                          e.preventDefault();
                          confirmDialog(
                            <RpConfirm
                              heading="Request Access to this Channel?"
                              message={`You are about to request access to the ${channel.name} channel for your account. Would you like to continue?`}
                            />,
                            () => handleRequestAccess(channel),
                            () => {}
                          );
                        }}
                      >
                        Request Access
                      </Link>
                    )}
                </Popover>
              </Box>
            </Grid>
          ))
        ) : shouldShowEmptyState ? (
          <Grid item>
            <Alert severity="info">
              <strong>No channel list data found.</strong>
            </Alert>
          </Grid>
        ) : null}

        {showToolTip && processedChannels.length > 0 && (
          <Grid item size={{ xs: "auto" }}>
            <RpMuiToolTip
              content={
                <span style={{ fontSize: 12 }}>
                  Want access to a new channel? Reach out to your brand manager
                </span>
              }
            >
              <Icon
                component={RpSvgIcon}
                name="green_info"
                color="primary"
                sx={{
                  width: 26,
                  height: 26,
                  display: { xs: "none", lg: "block" },
                }}
              />
            </RpMuiToolTip>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default ChannelList;
